import React from "react";
import { api } from "@/lib/api";
import { CAPABILITIES_PATH } from "./capabilities";
import { EntitlementsContext, buildEntitlements } from "./EntitlementsContext";

/**
 * Reads the tenant's capability map ONCE for the whole console and shares it.
 *
 * Mounted by AdminLayout, around both the sidebar (desktop column and mobile drawer) and
 * the page outlet, so the nav, the route guards and the pages answer "is this in my
 * plan?" from the same response and cannot disagree.
 *
 * `reload()` is the refresh a page calls after it changed what the tenant owns (a plan
 * change or an add-on purchase from Billing): the nav and the guards follow without a
 * page reload. A reload keeps showing the previous map until the new one arrives, so the
 * sidebar does not collapse and re-expand around a plan change.
 */
export default function EntitlementsProvider({ children }) {
  const [state, setState] = React.useState({ phase: "loading", planId: null, capabilities: null });
  const alive = React.useRef(true);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(CAPABILITIES_PATH);
      const capabilities = data?.capabilities;
      if (!alive.current) return;
      if (!capabilities || typeof capabilities !== "object" || Array.isArray(capabilities)) {
        // A payload that is not the contract's shape is a failed read, not an empty plan:
        // "nothing is included" would be a false statement about what the tenant bought.
        setState((s) => ({ ...s, phase: "error" }));
        return;
      }
      setState({ phase: "ready", planId: data.plan_id ?? null, capabilities });
    } catch {
      // A failed FIRST read is an error state (the guards offer a retry); a failed
      // refresh keeps the map already on screen, which is still the last known truth.
      if (alive.current) setState((s) => ({ ...s, phase: s.phase === "ready" ? "ready" : "error" }));
    }
  }, []);

  React.useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  const retry = React.useCallback(() => {
    setState((s) => (s.phase === "error" ? { ...s, phase: "loading" } : s));
    load();
  }, [load]);

  const value = React.useMemo(
    () => buildEntitlements({ phase: state.phase, planId: state.planId, capabilities: state.capabilities, reload: retry }),
    [state, retry],
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}
